import React, { useState } from 'react';

/**
 * Render a post-hoc multiple-comparison card displaying LSD, DMRT,
 * and Tukey HSD letter-groupings side-by-side. Auto-suppresses
 * itself when the corresponding ANOVA F-test was non-significant.
 *
 * Shape of `suite` (single ANOVA term):
 * {
 *   alpha: 0.05,
 *   auto_triggered: bool,
 *   comparison_label: 'Treatment' | 'Nitrogen (A)' | ...,
 *   n_treatments: int,
 *   n_replications: int,
 *   ms_error: float,
 *   df_error: int,
 *   treatment_p_value: float,
 *   anova_significant: bool,
 *   lsd: { method, critical_value, critical_value_label, formula, groups, interpretation },
 *   dmrt: { method, critical_value_label, formula, ssr_by_step, groups, interpretation },
 *   tukey_hsd: { method, critical_value, critical_value_label, formula, q_value, k_treatments, groups, interpretation },
 *   sorted_treatments: [[name, mean], ...],
 *   footer_note: string,
 * }
 *
 * `suite` may also be a dict-of-suites { 'A': {...}, 'B': {...}, 'A x B': {...} }
 * for multi-term ANOVA (Factorial designs). In that case we render
 * one card per term that was significant (or all, expandable).
 */
export function PostHocSuiteCard({ suite, testIdPrefix = 'posthoc', defaultExpanded = false }) {
  if (!suite || typeof suite !== 'object') return null;

  // Multi-term case (Factorial): keys are term labels, values are suite objects
  const isMultiTerm = !suite.lsd && !suite.dmrt && !suite.tukey_hsd;
  if (isMultiTerm) {
    const entries = Object.entries(suite).filter(([, v]) => v && (v.lsd || v.dmrt || v.tukey_hsd));
    if (entries.length === 0) return null;
    return (
      <div className="mt-4 space-y-3" data-testid={`${testIdPrefix}-multi`}>
        {entries.map(([termLabel, termSuite]) => (
          <SingleTermPostHocCard
            key={termLabel}
            suite={termSuite}
            termLabel={termLabel}
            testId={`${testIdPrefix}-${termLabel.replace(/\s+/g, '-').toLowerCase()}`}
            defaultExpanded={defaultExpanded}
          />
        ))}
      </div>
    );
  }
  return (
    <SingleTermPostHocCard
      suite={suite}
      termLabel={suite.comparison_label || 'Treatment'}
      testId={testIdPrefix}
      defaultExpanded={defaultExpanded}
    />
  );
}


function SingleTermPostHocCard({ suite, termLabel, testId, defaultExpanded }) {
  const significant = suite.anova_significant !== false; // default true
  const [expanded, setExpanded] = useState(defaultExpanded || significant);
  const methods = [
    { key: 'lsd', label: 'LSD', subtitle: 'Fisher 1935 — permissive' },
    { key: 'dmrt', label: 'DMRT', subtitle: "Duncan 1955 — recommended" },
    { key: 'tukey_hsd', label: 'Tukey HSD', subtitle: 'Tukey 1953 — conservative' },
  ];

  // Build name -> mean map for display
  const meansBySorted = Array.isArray(suite.sorted_treatments) ? suite.sorted_treatments : [];

  return (
    <div
      className="mt-4 rounded-xl"
      style={{
        border: significant ? '1px solid #B7DEC6' : '1px dashed #E2E8F0',
        background: significant ? '#F6FBF7' : '#F8FAFC',
      }}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left"
        data-testid={`${testId}-toggle`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span style={{
            display: 'inline-block', width: 9, height: 9, borderRadius: 99,
            background: significant ? '#16A34A' : '#94A3B8',
          }} />
          <div className="truncate">
            <div className="text-sm font-semibold text-[#2C2A28]">
              Mean Comparison: {termLabel}{' '}
              <span style={{ fontSize: 11, fontWeight: 500, color: significant ? '#15803D' : '#64748B' }}>
                {significant ? `(F p = ${(suite.treatment_p_value ?? 0).toFixed(4)} · auto-triggered)` : `(F p = ${(suite.treatment_p_value ?? 0).toFixed(4)} · advisory only)`}
              </span>
            </div>
            <div className="text-[11px] text-slate-500">
              LSD · DMRT · Tukey HSD letter-groupings · α = {suite.alpha ?? 0.05}
            </div>
          </div>
        </div>
        <span className="text-xs text-slate-400">{expanded ? '▼' : '▶'}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1">
          {/* Three-method comparison table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse mt-2" data-testid={`${testId}-table`}>
              <thead>
                <tr className="bg-[#2D6A4F] text-white">
                  <th className="px-3 py-2 text-left text-xs font-semibold border border-[#1B4332]">Rank</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold border border-[#1B4332]">{termLabel.split(' (')[0]}</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold border border-[#1B4332]">Mean</th>
                  {methods.map(m => (
                    <th key={m.key} className="px-3 py-2 text-center text-xs font-semibold border border-[#1B4332]" style={{ minWidth: 88 }}>
                      {m.label}
                      <div className="text-[9px] font-normal text-[#B7DEC6]">{m.subtitle}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {meansBySorted.map(([name, mean], idx) => (
                  <tr key={name} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#F0FAF0]'}>
                    <td className="px-3 py-1.5 text-left text-xs border border-[#E8E6E1] font-mono text-slate-500">{idx + 1}</td>
                    <td className="px-3 py-1.5 text-left text-xs border border-[#E8E6E1] font-semibold">{name}</td>
                    <td className="px-3 py-1.5 text-center font-mono text-xs border border-[#E8E6E1]">{typeof mean === 'number' ? mean.toFixed(4) : mean}</td>
                    {methods.map(m => (
                      <td key={m.key}
                        className="px-3 py-1.5 text-center font-bold text-xs border border-[#E8E6E1] text-emerald-700"
                        data-testid={`${testId}-${m.key}-${name.replace(/\s+/g, '_')}`}
                      >
                        {(suite[m.key]?.groups || {})[String(name)] || '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Critical value strip */}
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
            {methods.map(m => {
              const md = suite[m.key];
              if (!md) return null;
              return (
                <div key={m.key} className="rounded-lg border px-3 py-2"
                  style={{ borderColor: '#D5ECD5', background: '#FCFFFC' }}
                  data-testid={`${testId}-${m.key}-summary`}>
                  <div className="text-[10px] uppercase tracking-wider text-[#6B8E7B] font-semibold">{m.label}</div>
                  <div className="text-sm font-mono text-[#2D6A4F]">
                    {typeof md.critical_value === 'number' ? md.critical_value.toFixed(4) : '—'}
                    {md.critical_value_label && <span className="text-[10px] text-slate-500 ml-1">({md.critical_value_label})</span>}
                  </div>
                  {md.formula && <div className="text-[10px] text-slate-500 font-mono mt-0.5">{md.formula}</div>}
                </div>
              );
            })}
          </div>

          {/* DMRT SSR-by-step table — small, hidden under expandable summary */}
          {suite.dmrt?.ssr_by_step && (
            <details className="mt-2 text-xs" data-testid={`${testId}-dmrt-ssr-details`}>
              <summary className="cursor-pointer text-slate-500 hover:text-slate-700">DMRT SSR(p) values per rank distance</summary>
              <div className="mt-1 grid grid-cols-3 sm:grid-cols-6 gap-1">
                {Object.entries(suite.dmrt.ssr_by_step).map(([p, val]) => (
                  <div key={p} className="rounded bg-white border border-slate-100 px-2 py-1 text-center">
                    <div className="text-[9px] text-slate-400 uppercase">SSR({p})</div>
                    <div className="font-mono text-[11px] text-slate-700">{typeof val === 'number' ? val.toFixed(4) : val}</div>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Footer note + reference */}
          <p className="text-[11px] italic text-slate-500 mt-3" data-testid={`${testId}-footer`}>
            {suite.footer_note || 'Letters that share = not significantly different. References: Fisher 1935; Duncan 1955; Tukey 1953; Gomez & Gomez 1984.'}
          </p>
        </div>
      )}
    </div>
  );
}

export default PostHocSuiteCard;
